import { useState } from 'react';
import { api, ApiError, errorMessage } from '../../shared/api/client';
import { Alert, Button, Card } from '../../shared/ui/components';
import { NAME_PATTERN, tidyName } from '../../shared/geo/places';

interface Row {
  line: number;
  givenName: string;
  paternalSurname: string;
  maternalSurname: string;
  gender: 'femenino' | 'masculino' | null;
  problem: string | null;
}

type Outcome = { kind: 'ok' } | { kind: 'similar'; names: string } | { kind: 'error'; message: string };

const fullName = (r: Row) => [r.givenName, r.paternalSurname, r.maternalSurname].filter(Boolean).join(' ');

/** Una línea por persona: «Nombre(s), Apellido paterno, Apellido materno, F o M» (el materno puede ir vacío). */
export function parseLines(text: string): Row[] {
  return text
    .split('\n')
    .map((raw, i) => ({ raw: raw.trim(), line: i + 1 }))
    .filter((l) => l.raw)
    .map(({ raw, line }) => {
      const parts = raw.split(/[,;\t]/).map((p) => tidyName(p.trim().replace(/\s+/g, ' ')));
      const [givenName = '', paternalSurname = '', maternalSurname = '', sex = ''] = parts.length >= 4 ? parts : [parts[0], parts[1], '', parts[2]];
      const s = sex.toLowerCase();
      const gender = s.startsWith('f') || s.startsWith('mu') ? 'femenino' : s.startsWith('m') || s.startsWith('h') ? 'masculino' : null;
      const problem = !givenName
        ? 'Falta el nombre.'
        : !paternalSurname
          ? 'Falta el apellido paterno.'
          : !gender
            ? 'Falta el sexo (F o M).'
            : [givenName, paternalSurname, maternalSurname].some((v) => v && !NAME_PATTERN.test(v))
              ? 'Los nombres solo llevan letras.'
              : null;
      return { line, givenName, paternalSurname, maternalSurname, gender, problem };
    });
}

/**
 * Registrar varios a la vez con lo mínimo (nombre, apellidos y sexo), por ejemplo para asignarles
 * una promesa. Quien se parece a alguien de la iglesia no se crea: se avisa para revisarlo.
 */
export function BulkRegisterCard({ churchId, onDone, onCancel }: { churchId: string; onDone: () => void; onCancel: () => void }) {
  const [text, setText] = useState('');
  const [rows, setRows] = useState<Row[] | null>(null);
  const [results, setResults] = useState<Map<number, Outcome>>(new Map());
  const [busy, setBusy] = useState(false);

  async function send(row: Row, force: boolean): Promise<Outcome> {
    try {
      await api.post(`/multichurch/churches/${churchId}/members`, {
        givenName: row.givenName, paternalSurname: row.paternalSurname, maternalSurname: row.maternalSurname, gender: row.gender, email: null, nursery: null, force,
      });
      return { kind: 'ok' };
    } catch (e) {
      const similar = e instanceof ApiError && e.status === 409 ? (e.details?.similar as { name: string }[] | undefined) : undefined;
      if (similar?.length) return { kind: 'similar', names: similar.map((p) => p.name).join(', ') };
      return { kind: 'error', message: errorMessage(e) };
    }
  }

  async function registerAll() {
    if (!rows) return;
    setBusy(true);
    const next = new Map(results);
    for (const row of rows) {
      if (row.problem || next.get(row.line)?.kind === 'ok') continue;
      next.set(row.line, await send(row, false));
      setResults(new Map(next));
    }
    setBusy(false);
    onDone();
  }

  async function registerAnyway(row: Row) {
    setBusy(true);
    const outcome = await send(row, true);
    setResults((r) => new Map(r).set(row.line, outcome));
    setBusy(false);
    onDone();
  }

  const ready = rows?.filter((r) => !r.problem && results.get(r.line)?.kind !== 'ok').length ?? 0;
  const done = rows?.filter((r) => results.get(r.line)?.kind === 'ok').length ?? 0;

  return (
    <Card title="Registrar varios">
      <div className="form">
        <p className="muted small">
          Una persona por línea: <strong>Nombre(s), Apellido paterno, Apellido materno, F o M</strong>. Si no tiene apellido materno, déjalo vacío
          (por ejemplo «Alba, Lagarda, , F»). Quedan como miembros sin cuenta; lo demás lo completas después en su ficha.
        </p>
        {rows === null ? (
          <>
            <div className="field">
              <label htmlFor="bulk-lines">Personas</label>
              <textarea id="bulk-lines" rows={10} value={text} onChange={(e) => setText(e.target.value)} placeholder={'Esther, Ávalos, , F\nOscar, Villeda, , M'} />
            </div>
            <div className="actions">
              <Button type="button" disabled={!text.trim()} onClick={() => setRows(parseLines(text))}>Revisar</Button>
              <Button type="button" variant="ghost" onClick={onCancel}>Cancelar</Button>
            </div>
          </>
        ) : (
          <>
            <ul className="list">
              {rows.map((r) => {
                const out = results.get(r.line);
                return (
                  <li key={r.line} className="list__item">
                    <div style={{ flex: 1 }}>
                      <strong>{fullName(r) || `Línea ${r.line}`}</strong>
                      <span className="muted small"> · {r.gender === 'femenino' ? 'Femenino' : r.gender === 'masculino' ? 'Masculino' : 'sin sexo'}</span>
                      {r.problem ? <p className="small text-error">{r.problem}</p> : null}
                      {out?.kind === 'ok' ? <p className="small">Registrado.</p> : null}
                      {out?.kind === 'error' ? <p className="small text-error">{out.message}</p> : null}
                      {out?.kind === 'similar' ? (
                        <p className="small text-error">
                          No se registró: ya hay alguien parecido ({out.names}).{' '}
                          <button type="button" className="link-button" disabled={busy} onClick={() => void registerAnyway(r)}>
                            Es otra persona, registrarla
                          </button>
                        </p>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
            {done > 0 ? <Alert tone="success">{done === 1 ? 'Se registró 1 persona.' : `Se registraron ${done} personas.`}</Alert> : null}
            <div className="actions actions--wrap">
              <Button type="button" busy={busy} disabled={ready === 0} onClick={() => void registerAll()}>
                {ready === 1 ? 'Registrar 1 persona' : `Registrar ${ready} personas`}
              </Button>
              <Button type="button" variant="ghost" disabled={busy} onClick={() => { setRows(null); setResults(new Map()); }}>Editar la lista</Button>
              <Button type="button" variant="ghost" disabled={busy} onClick={onCancel}>Cerrar</Button>
            </div>
          </>
        )}
      </div>
    </Card>
  );
}
